import React, { useMemo, useState } from 'react';
import {
  useTable,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import PaginatedTable from './PaginatedTable';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';

import MobileList from './MobileList';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import useMediaQuery from '../helpers/useMediaQuery';
import type { AlpacaEvent } from '../models/AlpacaEvent';
import { Link } from 'react-router';

export default function AlpacasForEventTable({
  events,
  handleDelete,
  isEventEdit = false,
}: {
  events: AlpacaEvent[];
  handleDelete: (alpacaEventId: number) => void;
  isEventEdit?: boolean;
}) {
  const matches = useMediaQuery('screen and (max-width: 640px)');

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState('');

  const tableData = useMemo(() => events, [events]);
  const columnData: ColumnDef<AppTableFeatures, AlpacaEvent>[] = useMemo(
    () => [
      {
        header: 'Alpaca',
        accessorKey: 'alpaca.name',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 350,
        cell: ({ row }) => (
          <Link
            className='font-bold hover:opacity-70 dark:text-gray-300'
            to={`/alpacas/${row.original.alpaca?.alpacaId}`}
          >
            {row.original.alpaca?.name}
          </Link>
        ),
      },
      {
        header: 'Note',
        accessorKey: 'note',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        cell: ({ row }) => (
          <div className='whitespace-pre-line'>{row.original.note}</div>
        ),
      },
      {
        header: '',
        id: 'action',
        enableColumnFilter: false,
        enableSorting: false,
        size: 200,
        cell: ({ row }) => (
          <div className='flex items-center justify-around gap-2'>
            <Link
              className='font-bold hover:opacity-70 dark:text-gray-300'
              to={
                isEventEdit
                  ? `/events/${row.original.eventId}/alpaca-event/${row.original.alpacaEventId}?event-edit=true`
                  : `/events/${row.original.eventId}/alpaca-event/${row.original.alpacaEventId}`
              }
            >
              <div className='flex items-center gap-2'>
                <FaEdit /> Edit
              </div>
            </Link>

            <button
              onClick={() => handleDelete(row.original.alpacaEventId)}
              type='button'
              className='p-2 font-bold text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-500'
            >
              <div className='flex items-center gap-2'>
                <FaTrashAlt /> Delete
              </div>
            </button>
          </div>
        ),
      },
    ],
    [handleDelete, isEventEdit],
  );

  const columns = useMemo(() => columnData, [columnData]);

  const table = useTable({
    features: appTableFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: events.length > 9 ? 10 : events.length,
      },
    },
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    paginateExpandedRows: false,
  });

  if (matches) {
    return (
      <MobileList
        table={table}
        columnResizeMode='onChange'
        showFilters={true}
        showPagination={events.length > 9}
      />
    );
  }
  return (
    <PaginatedTable
      table={table}
      columnResizeMode='onChange'
      showFilters={true}
      allowWordWrap={true}
      showPagination={events.length > 9}
    />
  );
}
